import { useEffect, useRef, useState, useCallback } from "react";
import {
  AgentEventsEnum,
  LiveAvatarSession,
  SessionState,
  VoiceChatState,
} from "@heygen/liveavatar-web-sdk";
import { useAppContext } from "../context/AppContext";
import { getAiGreetingInstructionsMessage } from "../constants";
import { useLocation } from "react-router-dom";

type UseLiveAvatarSessionParams = {
  sessionToken: string | null;
  onDisconnected?: () => void;
  userName: string;
};

export const useLiveAvatarSession = ({
  sessionToken,
  onDisconnected,
  userName = ""
}: UseLiveAvatarSessionParams) => {
  const [sessionState, setSessionState] = useState<SessionState>(
    SessionState.INACTIVE
  );
  const [voiceChatState, setVoiceChatState] = useState<VoiceChatState>(
    VoiceChatState.INACTIVE
  );

  const [voiceChatMuted, setVoiceChatMuted] = useState(false);

  const [isVideoReady, setIsVideoReady] = useState(false);
  const [micPermissionError, setMicPermissionError] = useState(false);

  const [transcription, setTranscription] = useState<string>('');

  const [avatarSpeaking, setAvatarSpeaking] = useState(false);

  const { aiLanguage } = useAppContext();

  const location = useLocation();


  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sessionRef = useRef<LiveAvatarSession | null>(null);
  const lastAttachedElementRef = useRef<HTMLMediaElement | null>(null);
  const onDisconnectedRef = useRef<(() => void) | undefined>(onDisconnected);

  // Keep latest callback without re-running effects
  useEffect(() => {
    onDisconnectedRef.current = onDisconnected;
  }, [onDisconnected]);

  // Check microphone permission status once
  useEffect(() => {
    const checkMicPermission = async () => {
      try {
        if (navigator.permissions && navigator.permissions.query) {
          // `name: "microphone"` is not yet in lib.dom types everywhere, so cast
          const permissionStatus = await navigator.permissions.query(
            { name: "microphone" as PermissionName }
          );
          setMicPermissionError(permissionStatus.state === "denied");
          permissionStatus.onchange = () => {
            setMicPermissionError(permissionStatus.state === "denied");
          };
        }
      } catch {
        // Best-effort only; real errors will be caught when starting voice chat
      }
    };

    void checkMicPermission();
  }, []);

  // Create and manage the LiveAvatar session
  useEffect(() => {
    if (!sessionToken) {
      return undefined;
    }

    const session: any = new LiveAvatarSession(sessionToken);
    sessionRef.current = session;

    let isAttached = false;

    session.on("session.state_changed", (state: SessionState) => {
      setSessionState(state);
      if (state === SessionState.DISCONNECTED && onDisconnectedRef.current) {
        onDisconnectedRef.current();
      }
    });

    session.on("session.stream_ready", () => {
      if (isAttached) return;

      const mediaTarget: HTMLMediaElement | null =
        videoRef.current || audioRef.current;

      if (!mediaTarget) {
        return;
      }

      try {
        session.attach(mediaTarget);
        isAttached = true;
        lastAttachedElementRef.current = mediaTarget;
        setIsVideoReady(Boolean(videoRef.current));
        setAvatarSpeaking(true);
      } catch (err) {
        console.error("❌ [LiveAvatar] Failed to attach media element:", err);
      }
    });

    session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
      setAvatarSpeaking(true);
    });

    session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
      setAvatarSpeaking(false);
    });
    session.on(AgentEventsEnum.AVATAR_TRANSCRIPTION, (data) => {
      if (data.text.includes("|")) {
        sessionRef.current?.repeat(getAiGreetingInstructionsMessage(aiLanguage, userName));
      }
      setTranscription(data.text.includes("|") ? "" : data.text);
    });

    session.on("error", (error: unknown) => {
      console.error("❌ [LiveAvatar] Session error:", error);
    });

    let voiceChatInterval: number | null = null;
    if (session.voiceChat) {
      const voiceChat = session.voiceChat;
      voiceChatInterval = window.setInterval(() => {
        if (voiceChat.state !== undefined) {
          setVoiceChatState(voiceChat.state as VoiceChatState);
        }
      }, 500);
    }

    session.start().catch((err: unknown) => {
      console.error("❌ [LiveAvatar] Failed to start session:", err);
      setSessionState(SessionState.DISCONNECTED);
    });

    return () => {
      if (voiceChatInterval !== null) {
        window.clearInterval(voiceChatInterval);
      }
      if (session) {
        session.stop().catch(() => { });
      }
    };
  }, [sessionToken]);

  // Re-attach media element when UI mounts again (e.g. modal reopened or bubble only)
  useEffect(() => {
    const session = sessionRef.current;
    const videoElement = videoRef.current;
    const audioElement = audioRef.current;

    if (!session) {
      return;
    }

    const target: HTMLMediaElement | null = videoElement || audioElement;
    if (!target) {
      return;
    }

    if (lastAttachedElementRef.current === target) {
      return;
    }

    try {
      session.attach(target);
      lastAttachedElementRef.current = target;
      setIsVideoReady(Boolean(videoElement));
    } catch (err) {
      console.error("❌ [LiveAvatar] Failed to re-attach media element:", err);
    }
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (sessionRef.current) {
        sessionRef.current.keepAlive();
      }
    }, 10000)
    return () => clearInterval(interval);
  }, [])

  useEffect(() => {
    attachVideo();
  }, [location])

  const sendMessage = useCallback((message: string) => {
    if (!message || !message.trim() || !sessionRef.current) return;
    try {
      sessionRef.current.message(message.trim());
    } catch (err) {
      console.error("❌ [LiveAvatar] Failed to send message:", err);
    }
  }, []);

  const repeat = useCallback((text: string) => {
    if (!text.trim() || !sessionRef.current) return;
    try {
      sessionRef.current.repeat(text.trim());
    } catch (err) {
      console.error("❌ [LiveAvatar] Failed to repeat:", err);
    }
  }, []);

  const startVoiceChat = useCallback(async () => {
    const currentSession = sessionRef.current as
      | (LiveAvatarSession & {
        voiceChat?: {
          start: () => Promise<void>;
          stop: () => void;
          state?: VoiceChatState;
        };
      })
      | null;

    if (!currentSession || !currentSession.voiceChat) {
      return;
    }

    try {
      setMicPermissionError(false);
      await currentSession.voiceChat.start();
      await currentSession.voiceChat.unmute();
      setVoiceChatMuted(false);
    } catch (err) {
      const error = err as { name?: string; message?: string };
      if (
        error.name === "NotAllowedError" ||
        error.message?.includes("Permission denied")
      ) {
        setMicPermissionError(true);
      }
      console.error("❌ [LiveAvatar] Failed to start voice chat:", err);
    }
  }, []);

  const attachVideo = useCallback(() => {
    if (!videoRef.current) return;
    sessionRef.current?.attach(videoRef.current);
  }, [videoRef.current, sessionRef.current])

  const stopVoiceChat = useCallback(() => {
    const currentSession = sessionRef.current as
      | (LiveAvatarSession & {
        voiceChat?: {
          start: () => Promise<void>;
          stop: () => void;
          state?: VoiceChatState;
        };
      })
      | null;

    if (!currentSession || !currentSession.voiceChat) {
      return;
    }
    try {
      // currentSession.voiceChat.stop();
      currentSession.voiceChat.mute();
      setVoiceChatMuted(true);

    } catch (err) {
      console.error("❌ [LiveAvatar] Failed to stop voice chat:", err);
    }
  }, []);

  const interruptSpeech = useCallback((text: string) => {
    if (!sessionRef.current) return;
    try {
      sessionRef.current.interrupt();
    } catch (err) {
      console.error("❌ [LiveAvatar] Failed to interrupt speech:", err);
    }
  }, []);

  const isConnected = sessionState === SessionState.CONNECTED;
  const isVoiceChatActive = voiceChatState === VoiceChatState.ACTIVE;

  return {
    // refs
    videoRef,
    audioRef,

    // state
    sessionState,
    voiceChatState,
    isVideoReady,
    micPermissionError,
    isConnected,
    isVoiceChatActive,
    transcription,
    avatarSpeaking,
    voiceChatMuted,
    // actions
    sendMessage,
    repeat,
    startVoiceChat,
    stopVoiceChat,
    setMicPermissionError,
    interruptSpeech,
  };
};

